// Sunrise / sunset + day-progress — pure astronomy, no network, no DOM.
// Compact port of the well-known NOAA / Meeus algorithm (à la SunCalc, MIT).
// Accurate to ~1 minute, which is all the sun-arc track needs. RN-portable.

const rad = Math.PI / 180
const dayMs = 86400000
const J1970 = 2440588
const J2000 = 2451545
const e = rad * 23.4397 // obliquity of the ecliptic

const toJulian = (date: Date) => date.valueOf() / dayMs - 0.5 + J1970
const fromJulian = (j: number) => new Date((j + 0.5 - J1970) * dayMs)
const toDays = (date: Date) => toJulian(date) - J2000

const solarMeanAnomaly = (d: number) => rad * (357.5291 + 0.98560028 * d)
const eclipticLongitude = (M: number) => {
  const C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M))
  const P = rad * 102.9372 // perihelion of Earth
  return M + C + P + Math.PI
}
const declination = (l: number) => Math.asin(Math.sin(e) * Math.sin(l))

const J0 = 0.0009
const approxTransit = (Ht: number, lw: number, n: number) => J0 + (Ht + lw) / (2 * Math.PI) + n
const solarTransitJ = (ds: number, M: number, L: number) =>
  J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L)
const hourAngle = (h: number, phi: number, d: number) =>
  Math.acos((Math.sin(h) - Math.sin(phi) * Math.sin(d)) / (Math.cos(phi) * Math.cos(d)))

export interface SunTimes {
  sunrise: Date | null
  sunset: Date | null
  solarNoon: Date
  /** When there is no rise/set: the sun is up all day or down all day. */
  polar: 'day' | 'night' | null
}

/** Sunrise / sunset for the given instant's day at lat/lon. */
export function sunTimes(date: Date, lat: number, lng: number): SunTimes {
  const lw = rad * -lng
  const phi = rad * lat
  const d = toDays(date)
  const n = Math.round(d - J0 - lw / (2 * Math.PI))
  const ds = approxTransit(0, lw, n)
  const M = solarMeanAnomaly(ds)
  const L = eclipticLongitude(M)
  const dec = declination(L)
  const Jnoon = solarTransitJ(ds, M, L)
  const solarNoon = fromJulian(Jnoon)

  const h0 = -0.833 * rad // standard refraction + solar radius
  const cosw = (Math.sin(h0) - Math.sin(phi) * Math.sin(dec)) / (Math.cos(phi) * Math.cos(dec))
  if (cosw <= -1) return { sunrise: null, sunset: null, solarNoon, polar: 'day' }
  if (cosw >= 1) return { sunrise: null, sunset: null, solarNoon, polar: 'night' }

  const w0 = hourAngle(h0, phi, dec)
  const a = approxTransit(w0, lw, n)
  const Jset = solarTransitJ(a, M, L)
  const Jrise = Jnoon - (Jset - Jnoon)
  return { sunrise: fromJulian(Jrise), sunset: fromJulian(Jset), solarNoon, polar: null }
}

export interface DayProgress {
  /** 0 at sunrise → 1 at sunset; clamped. */
  fraction: number
  isDay: boolean
}

/** Where `now` sits between today's sunrise and sunset (for the sun-arc track). */
export function dayProgress(now: Date, t: SunTimes): DayProgress {
  if (t.polar === 'day') return { fraction: 0.5, isDay: true }
  if (t.polar === 'night' || !t.sunrise || !t.sunset) return { fraction: 0, isDay: false }
  const a = t.sunrise.getTime()
  const b = t.sunset.getTime()
  const f = (now.getTime() - a) / (b - a)
  return { fraction: Math.max(0, Math.min(1, f)), isDay: f >= 0 && f <= 1 }
}
